import React from "react";
import styled from "styled-components";
import ResponsiveImage from "../ImageWrapper/ResponsiveImage";
import { FaStar } from "react-icons/fa";
import SidesBox from "./SidesBox";

const MenuCategory = ({ menuSection, menuType, t, config }) => {
  // Get the section data from the config
  const sectionConfig = config[menuSection];
  if (!sectionConfig) return null;

  // Format price based on whether it's a nested object or a simple value
  const formatPrice = (price) => {
    if (typeof price === 'object' && price !== null) {
      // Handle nested price object (e.g., small and large salad prices)
      if (price.small && price.large) {
        return `${price.large.toFixed(2)} / ${price.small.toFixed(2)}`;
      }
      return Object.values(price).join(' / ');
    } else if (typeof price === 'number') {
      // Handle simple price value
      return price.toFixed(2);
    }
    // Protect against nil or undefined values
    if (price === null || price === undefined) {
      return "";
    }
    return price;
  };

  // Determine the correct translation path based on menu type and section
  let translationPath;
  let configType;

  if (menuType.includes("Breakfast")) {
    configType = "breakfast";
    translationPath = `breakfast.categories.${menuSection}`;
  } else {
    configType = "main";
    translationPath = `main.categories.${menuSection}`;
  }

  // Get category title and description from translations
  const categoryTitle = t(`${translationPath}.title`, sectionConfig.title || menuSection.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()));
  const categoryDescription = t(`${translationPath}.description`, "");

  return (
    <CategoryContainer>
      <div className="item-header">
        {menuType.includes("cash") && (
          <>
            <p className="mb-2">
              <span>
                <span className="star-icon-header">
                  <FaStar />
                </span>{" "}
                {t("eatAndPlay.availableOnTables", "Available on tables")}
              </span>
            </p>
            {(sectionConfig.sideGroup === "breakfastSides" || sectionConfig.sideGroup === "regularSides") && (
              <p className="tables-of-choice mb-3">
                {t("eatAndPlay.tablesOfChoice")}
              </p>
            )}
          </>
        )}
        {sectionConfig.image && (
          <ResponsiveImage src={sectionConfig.image} alt={categoryTitle} />
        )}
        <h3>{categoryTitle}</h3>
      </div>
      {categoryDescription && <CategoryDescription>{categoryDescription}</CategoryDescription>}

      {/* Render menu items */}
      {sectionConfig.items && Object.entries(sectionConfig.items).map(([itemKey, itemData], index) => {
        const itemTranslationPath = `${translationPath}.items.${itemKey}`;
        // Try to get the title from translations, fallback to capitalized key if not found
        const itemTitle = t(`${itemTranslationPath}.title`, itemKey.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase()));
        const itemDescription = t(`${itemTranslationPath}.description`, "", { returnObjects: true }
        );

        return (
          <div className="item-row" key={index}>
            <ItemHeader>
              <ItemTitle>
                {itemTitle}
                {menuType.includes("cash") && itemData.starred && (
                  <span className="star-icon">
                    <FaStar />
                  </span>
                )}
                {menuType.includes("tournament") && (
                  <span className="price">
                    {formatPrice(itemData.price)}
                  </span>
                )}
              </ItemTitle>
            </ItemHeader>
            {itemDescription && (
              <ItemDescription>
                {typeof itemDescription === 'object' || Array.isArray(itemDescription)
                  ? Object.entries(itemDescription).map(([key, value], i, arr) => {
                      // Get the config for this option to check if it's starred
                      const optionConfig = sectionConfig.items[itemKey]?.options?.[key];
                      const isStarred = optionConfig?.starred || false;

                      return (
                        <React.Fragment key={key}>
                          {value}
                          {menuType.includes("cash") && isStarred && (
                            <span className="star-icon">
                              <FaStar />
                            </span>
                          )}
                          {i < arr.length - 1 ? ', ' : ''}
                        </React.Fragment>
                      );
                    })
                  : itemDescription
                }
              </ItemDescription>
            )}
          </div>
        );
      })}

      {/* Render extras if applicable */}
      {sectionConfig.sideGroup && sectionConfig.sideGroup !== "none" && (
        <SidesBox
          sideGroup={sectionConfig.sideGroup}
          config={config}
          t={t}
          menuType={menuType}
          formatPrice={formatPrice}
        />
      )}
    </CategoryContainer>
  );
};

// Styled components
const CategoryContainer = styled.div`
  break-inside: avoid;
  margin-bottom: 1rem;
  background: var(--lightblack);
  padding: 20px;

  & img {
    width: 24px;
    height: 24px;
  }

  & > p {
    text-transform: uppercase;
    text-align: center;
    margin-top: 10px;
  }

  div.item-header {
    display: flex;
    flex-direction: column;
    margin-bottom: 5px;

    & > p {
      display: flex;
      flex-direction: column;
      align-items: center;
      line-height: 20px;
      font-weight: 200;
      font-size: 14px;
      text-align: center;
      margin-bottom: 15px;

      .star-icon-header {
        color: var(--mustard);
        margin-right: 5px;
        font-size: 14px;
        display: inline-block;
        position: relative;
        bottom: 2px;
      }
    }

    .tables-of-choice {
      text-align: center;
      font-size: 14px;
      margin-bottom: 15px;
    }

    & > img {
      object-fit: cover;
      object-position: center;
      width: 100%;
      height: 230px;
    }

    & > h3 {
      margin: 0;
      text-align: center;
      background: var(--red);
      padding: 10px;
      text-transform: uppercase;
      font-size: 18px;
    }
  }

  & > h3 {
    margin: 0;
    margin-top: 20px;
  }

  .item-row {
    display: flex;
    flex-direction: column;
    padding: 15px 0;
    border-bottom: 1px solid var(--rewards_grid);
    &:last-child {
      border-bottom: none;
      padding-bottom: 0;
    }
  }


  .star-icon {
    color: var(--mustard);
    margin-left: 5px;
    font-size: 12px;
    display: inline-block;
    position: relative;
    bottom: 2px;
  }

  @media screen and (max-width: 991px) {
    padding: 10px;
    margin-top: 20px;
  }
`;

const CategoryDescription = styled.div`
  margin: 10px 0;
  font-size: 14px;
  color: var(--mutedwhite);
  text-align: center;
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ItemTitle = styled.h6`
  margin: 0 0 8px;
  text-transform: uppercase;
  line-height: normal;
  display: flex;
  align-items: baseline;
  width: 100%;
  .price {
    white-space: nowrap;
    color: var(--red);
    margin-left: 1rem;
  }
`;

const ItemDescription = styled.div`
  margin-bottom: 5px;
  font-size: 14px;
  color: var(--mutedwhite);
`;


export default MenuCategory;
